import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';

export const SNIPPET_CARD_HEIGHT = 100;

const styles = cssMap({
	cardWrapper: {
		position: 'relative',
		display: 'flex',
		width: '100%',
		height: '100px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	cardWrapperActive: {
		backgroundColor: token('elevation.surface.hovered'),
	},
	cardButtonOverlay: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
		backgroundColor: 'transparent',
		textAlign: 'left',
	},
	cardContentLayer: {
		position: 'relative',
		width: '100%',
		zIndex: 1,
		pointerEvents: 'none',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

export type SnippetPanelCardProps = {
	children: React.ReactNode;
	onClick: () => void;
	ariaLabel: string;
	testId: string;
	isActive?: boolean;
	onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
	onMouseLeave?: () => void;
};

/**
 * Shared presentational card for snippet panel list rows (snippet items and create row).
 * Renders a clickable wrapper with overlay and a content layer (preview + custom content).
 */
export const SnippetPanelCard = ({
	children,
	onClick,
	ariaLabel,
	testId,
	isActive = false,
	onMouseEnter,
	onMouseLeave,
}: SnippetPanelCardProps) => {
	return (
		<Box
			xcss={cx(styles.cardWrapper, isActive && styles.cardWrapperActive)}
			testId={`${testId}-card-wrapper`}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<Pressable
				xcss={styles.cardButtonOverlay}
				onClick={onClick}
				testId={testId}
				aria-label={ariaLabel}
			/>
			<Inline
				xcss={styles.cardContentLayer}
				alignInline="start"
				alignBlock="center"
				space="space.150"
			>
				{children}
			</Inline>
		</Box>
	);
};
